import {
  parseBrandDiscoveryResponse,
  type BrandDiscoveryResponse,
} from '@/pages/BrandOffersPage/brandDiscovery.utils';
import { parseOffersListResponse } from '@/pages/BrandOffersPage/offersListResponse.utils';
import slaydApi from '@/services/slaydAPI/axiosInstance';
import type {
  Brand,
  BrandOfferPayload,
  BrandOfferResponse,
  OffersListResult,
} from '@/types/BrandOffer.types';

export interface BrandOffersAdminLoginRequest {
  username: string;
  password: string;
}

export interface BrandOffersAdminLoginResult {
  token: string;
  username: string;
}

export const apiBrandOffersAdminLogin = async (
  body: BrandOffersAdminLoginRequest
): Promise<BrandOffersAdminLoginResult> => {
  try {
    const response = await slaydApi.post<Record<string, unknown>>(
      '/account/brand-offers/login/',
      body
    );

    const data = response.data;
    const token = data.token ?? data.key ?? data.access;

    if (typeof token !== 'string' || token.length === 0) {
      throw new Error('Invalid response');
    }

    const username =
      typeof data.username === 'string' && data.username.length > 0
        ? data.username
        : body.username.trim();

    return { token, username };
  } catch (error) {
    console.error(error);
    throw new Error('Sign in failed');
  }
};

export interface GetBrandOffersParams {
  offer_type?: string;
  brand_id?: number;
  is_active?: boolean | string;
  ordering?: string;
  page?: number;
  page_size?: number;
  items_per_page?: number;
  [key: string]: string | number | boolean | undefined;
}

export const getBrandOffers = async (
  params?: GetBrandOffersParams
): Promise<OffersListResult> => {
  try {
    const response = await slaydApi.get<unknown>('/api/offers/', { params });

    const requestedPage = Math.max(1, Number(params?.page ?? 1));
    const itemsPerPage = Math.max(
      1,
      Number(params?.page_size ?? params?.items_per_page ?? 20)
    );

    return parseOffersListResponse(response.data, {
      requestedPage,
      itemsPerPage,
    });
  } catch (error) {
    console.error(error);
    throw new Error('Error fetching offers');
  }
};

export const fetchBrandOffer = async (
  id: number
): Promise<BrandOfferResponse> => {
  try {
    const response = await slaydApi.get<BrandOfferResponse>(
      `/api/offers/${id}/`
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(`Error fetching offer ${id}`);
  }
};

export const createBrandOffer = async (
  payload: BrandOfferPayload
): Promise<BrandOfferResponse> => {
  try {
    const response = await slaydApi.post<BrandOfferResponse>(
      '/api/offers/',
      payload
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Error creating offer');
  }
};

export const putBrandOffer = async (
  id: number,
  payload: BrandOfferPayload
): Promise<BrandOfferResponse> => {
  try {
    const response = await slaydApi.put<BrandOfferResponse>(
      `/api/offers/${id}/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(`Error updating offer ${id}`);
  }
};

export const patchBrandOffer = async (
  id: number,
  payload: Partial<BrandOfferPayload>
): Promise<BrandOfferResponse> => {
  try {
    const response = await slaydApi.patch<BrandOfferResponse>(
      `/api/offers/${id}/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(`Error patching offer ${id}`);
  }
};

export const deleteBrandOffer = async (id: number): Promise<void> => {
  try {
    await slaydApi.delete(`/api/offers/${id}/`);
  } catch (error) {
    console.error(error);
    throw new Error(`Error deleting offer ${id}`);
  }
};

export const fetchBrandsForOffers = async (): Promise<Brand[]> => {
  try {
    const response = await slaydApi.get<BrandDiscoveryResponse>(
      '/account/brand-discovery/'
    );
    return parseBrandDiscoveryResponse(response.data ?? {});
  } catch (error) {
    console.error(error);
    throw new Error('Error fetching brands');
  }
};
